/**
 * Voice Traffic utilities for formatting labels and package information
 */

/**
 * Format voice traffic package label with different display modes
 * @param {Object} packageData - Package data from backend
 * @param {string} displayMode - 'summary' | 'detailed' | 'compact'
 * @param {Function} t - Translation function
 * @param {Object} options - Additional options
 * @returns {string} Formatted label
 */
export const formatVoiceTrafficLabel = (packageData, displayMode = 'detailed', t, options = {}) => {
  if (!packageData) return '';

  // Handle custom amount mode
  if (packageData.useCustomAmount) {
    return t('calculator.voiceTrafficCustomAmount', 'Deposit for outbound calls');
  }

  // Extract data (support both structured and string formats)
  let countryCode, minutes, pricePerMinute, currency, overageMinutes = 0, overageRate = 0;

  if (packageData.countryCode) {
    // Structured data (preferred)
    countryCode = packageData.countryCode;
    minutes = packageData.minutes || 0;
    pricePerMinute = packageData.priceLocal || 0;
    currency = packageData.currency || 'USD';
    
    // Calculate overage if requested minutes > package minutes
    const requestedMinutes = options.requestedMinutes || minutes;
    overageMinutes = Math.max(0, requestedMinutes - minutes);
    overageRate = packageData.overageRate || pricePerMinute; // Fallback to same rate
  } else if (packageData.packageName) {
    // Parse existing package name format: "Ukraine 1000 min. (0.50 UAH / min.)"
    const match = packageData.packageName.match(/^(\w+)\s+(\d+)\s+min\.\s+\(([^)]+)\)$/);
    if (!match) return packageData.packageName; // Return original if can't parse
    
    const [, country, mins, priceText] = match;
    countryCode = getCountryCodeFromName(country);
    minutes = parseInt(mins, 10);
    
    // Extract price and currency from price text
    const priceMatch = priceText.match(/([\d.]+)\s+(\w+)/);
    if (priceMatch) {
      pricePerMinute = parseFloat(priceMatch[1]);
      currency = priceMatch[2];
    }
  } else {
    return ''; // No valid data
  }

  // Get translated country name
  const countryName = getTranslatedCountryName(countryCode, t);
  const minutesUnit = t('calculator.voiceTrafficMinutesShort', 'min.');
  const currencyUnit = getCurrencyUnit(currency, t);

  switch (displayMode) {
    case 'summary':
      // For Pricing Summary: "Пакет Казахстан 1000 хвилин + 300 поза пакетом"
      if (overageMinutes > 0) {
        return `${t('calculator.voiceTrafficPackage', 'Пакет')} ${countryName} ${minutes} ${t('calculator.voiceTrafficMinutesShort', 'хв.')} + ${overageMinutes} ${t('calculator.voiceTrafficOverageText', 'поза пакетом')}`;
      }
      return `${t('calculator.voiceTrafficPackage', 'Пакет')} ${countryName} ${minutes} ${t('calculator.voiceTrafficMinutesShort', 'хв.')}`;

    case 'detailed':
      // For OfferPage: "Казахстан 1000 хв. (10 тенге/хв) +300 хв. зверх пакету (10 тенге/хв)"
      let label = `${countryName} ${minutes} ${minutesUnit} (${pricePerMinute} ${currencyUnit})`;
      if (overageMinutes > 0) {
        label += ` +${overageMinutes} ${minutesUnit} ${t('calculator.voiceTrafficOverageText', 'зверх пакету')} (${overageRate} ${currencyUnit})`;
      }
      return label;

    case 'compact':
      // For compact displays: "Казахстан 1000 хв."
      return `${countryName} ${minutes} ${minutesUnit}`;

    case 'title':
      // For product titles: "Пакет хвилин Україна"
      return `${t('calculator.voiceTrafficLabel', 'Пакет хвилин')} ${countryName}`;

    default:
      return formatVoiceTrafficLabel(packageData, 'detailed', t, options);
  }
};

/**
 * Get translated country name from country code
 * @param {string} countryCode - Country code (UA, KZ, PL)
 * @param {Function} t - Translation function
 * @returns {string} Translated country name
 */
const getTranslatedCountryName = (countryCode, t) => {
  const countryTranslations = {
    'UA': t('countries.ukraine', 'Україна'),
    'KZ': t('countries.kazakhstan', 'Казахстан'), 
    'PL': t('countries.poland', 'Польща')
  };
  return countryTranslations[countryCode] || countryCode;
};

/**
 * Get currency unit translation
 * @param {string} currency - Currency code (UAH, KZT, PLN)
 * @param {Function} t - Translation function
 * @returns {string} Translated currency unit
 */
const getCurrencyUnit = (currency, t) => {
  const normalized = (currency || '').toUpperCase();
  const currencyUnits = {
    'UAH': t('calculator.voiceTrafficCurrencyUAH', 'UAH/min'),
    'KZT': t('calculator.voiceTrafficCurrencyKZT', 'KZT/min'),
    'EUR': t('calculator.voiceTrafficCurrencyEUR', '€/min'),
    'PLN': t('calculator.voiceTrafficCurrencyPLN', 'PLN/min')
  };
  return currencyUnits[normalized] || `${currency}/min`;
};

/**
 * Get country code from country name (for parsing legacy packageName)
 * @param {string} countryName - Country name in English
 * @returns {string} Country code
 */
const getCountryCodeFromName = (countryName) => {
  const nameToCode = {
    'Ukraine': 'UA',
    'Kazakhstan': 'KZ',
    'Poland': 'PL'
  };
  return nameToCode[countryName] || countryName;
};